/* eslint-disable no-restricted-globals */
import './_styles.scss';
import './_styles.dark-mode.scss';
import React, { useEffect, useState, useRef } from 'react';
import { Tab, Fade, Grid } from 'react-bootstrap';
import { useLocation, useHistory } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import qs from 'qs';

import Home from './components/Home';
import ArticlesList from './components/Articles/List';
import ArticleItem from './components/Articles/Item';

import { getGuidesList } from '../../services/endpoints/general';
import { prepareSearchList, detectTab } from './helpers';

const Guides = () => {
  const location = useLocation();
  const history = useHistory();

  const listRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [guidesList, setGuidesList] = useState([]);
  const [searchShadow, setSearchShadow] = useState(false);
  const [isLoadingSearch, setIsLoadingSearch] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [activeKey, setActiveKey] = useState('Home');
  const [search, setSearch] = useState('');
  const [searchList, setSearchList] = useState([]);

  const [debounceSearch] = useDebounce(search, 300);

  const handler = (event, items) => {
    if (event.data?.type === 'guides') {
      if (event.data?.data?.anchor) {
        const [articleId] = event.data?.data?.anchor?.split('_');
        if (!isNaN(articleId)) {
          const findedCategory = items?.find((item) => item?.articles?.find((art) => art.id === Number(articleId)));
          if (findedCategory) {
            const findedArticle = findedCategory?.articles?.find((art) => art.id === Number(articleId));
            if (findedArticle) {
              setActiveKey(`${findedCategory.category_name}.${findedArticle.title}`);
              history.replace({ pathname: location.pathname, search: `?anchor=${event.data?.data?.anchor}` });
            }
          }
        }
      }
    }
  };

  const getGuides = async () => {
    const search = qs.parse(location.search.slice(1));
    setIsLoading(true);
    try {
      const response = await getGuidesList();
      setGuidesList(response.data.guides);
      window.addEventListener('message', (event) => handler(event, response.data.guides));
      if (search?.pn) {
        const findFirstCategory = response?.data?.guides?.find((g) => search?.pn?.includes(g.access_url));
        const firstKey = activeKey?.split('.')?.[0];
        if (findFirstCategory && findFirstCategory?.category_name !== firstKey) {
          setActiveKey(findFirstCategory?.category_name);
          return history.replace({ pathname: location.pathname });
        }
      }
      if (search?.anchor) {
        const [articleId] = search?.anchor?.split('_');
        if (!isNaN(articleId)) {
          const findedCategory = response?.data?.guides?.find((item) => item?.articles?.find((art) => art.id === Number(articleId)));
          if (findedCategory) {
            const findedArticle = findedCategory?.articles?.find((art) => art.id === Number(articleId));
            if (findedArticle) {
              setActiveKey(`${findedCategory.category_name}.${findedArticle.title}`);
            }
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const getGuidesOnSearch = async (search) => {
    setIsLoadingSearch(true);
    setSearchList([]);
    try {
      const response = await getGuidesList(search);
      setSearchList(prepareSearchList(response.data.guides, debounceSearch, guidesList));
      setIsLoadingSearch(false);
    } catch (error) {
      setIsLoadingSearch(false);
    }
  };

  const onSelectBreadcrumbs = (event, crumb) => {
    event.preventDefault();
    if (crumb.active) {
      return;
    }

    setActiveKey(crumb.name);
  };

  const onSelectTab = (type, tabData) => {
    switch (type) {
    case 'home':
      setBreadcrumbs([]);
      break;

    case 'names':
      setBreadcrumbs([
        { name: 'Home', active: false },
        { name: tabData.category_name, active: true },
      ]);
      break;

    case 'categories':
      setBreadcrumbs([
        { name: 'Home', active: false },
        { name: tabData.categoryName, active: false },
        { name: tabData.title, active: true },
      ]);
      break;

    default:
      break;
    }
  };

  const onChangeSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleScrollBody = (e) => {
    if (e?.target?.scrollTop > 0) {
      setSearchShadow(true);
    } else {
      setSearchShadow(false);
    }
  };

  useEffect(() => {
    detectTab(guidesList, activeKey, onSelectTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey]);

  useEffect(() => {
    if (debounceSearch.trim()) {
      getGuidesOnSearch({ query: debounceSearch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearch]);

  useEffect(() => {
    document.title = 'Guides';
    getGuides();

    return () => window.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="guides-page">
      <Fade in={!!breadcrumbs?.length} timeout={250} unmountOnExit>
        <ul className="guides__breadcrumbs">
          {breadcrumbs.map((crumb, index) => (
            <li className={`guides__breadcrumbs-item${crumb.active ? ' active' : ''}`} key={index}>
              <a href="# " title={crumb.name} className="guides__breadcrumbs-link" onClick={(e) => onSelectBreadcrumbs(e, crumb)}>
                {crumb.name === 'Home' ? <span className="fas fa-house home-icon" /> : crumb.name}
              </a>
            </li>
          ))}
        </ul>
      </Fade>
      <Grid>
        <div className="guides" onScroll={handleScrollBody} ref={listRef}>
          {isLoading ? (
            <div className="spinner spinner-32" />
          ) : (
            <Tab.Container id="navbar-guides-tabs" activeKey={activeKey} onSelect={() => ({})}>
              <Tab.Content animation unmountOnExit>
                <Home
                  guides={guidesList}
                  filteredGuides={searchList}
                  search={search}
                  debounceSearch={debounceSearch}
                  onChangeSearch={onChangeSearch}
                  onSelectKey={setActiveKey}
                  onExited={() => setSearch('')}
                  searchShadow={searchShadow}
                  isLoading={isLoadingSearch}
                />

                <ArticlesList guides={guidesList} onSelectKey={setActiveKey} />

                <ArticleItem guides={guidesList} onSelectKey={setActiveKey} />
              </Tab.Content>
            </Tab.Container>
          )}
        </div>
      </Grid>
    </div>
  );
};

export default Guides;
